import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { CalendarEvent } from '../../types/calendar';
import { Calendar as CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';

export default function CalendarWidget() {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    // Get today's date at midnight
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Get end of today
    const endOfDay = new Date(today);
    endOfDay.setHours(23, 59, 59, 999);

    // Subscribe to today's events
    const unsubscribe = onSnapshot(
      query(
        collection(db, 'events'),
        where('start', '>=', today.toISOString()),
        where('start', '<=', endOfDay.toISOString()),
        orderBy('start', 'asc'),
        limit(5)
      ),
      (snapshot) => {
        const eventData: CalendarEvent[] = [];
        snapshot.forEach((doc) => {
          const event = { id: doc.id, ...doc.data() } as CalendarEvent;
          // Only show public events or private events created by the current user
          if (event.visibility === 'public' || event.createdBy === user.uid) {
            eventData.push(event);
          }
        });
        setEvents(eventData);
        setLoading(false);
      },
      (error) => {
        console.error('Error loading events:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const getEventTypeStyle = (type: CalendarEvent['type']) => {
    switch (type) {
      case 'event':
        return 'bg-primary-100 text-primary-700';
      case 'task':
        return 'bg-accent-100 text-accent-700';
      case 'reminder':
        return 'bg-secondary-100 text-secondary-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="bg-surface-light rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-text-primary">Today's Schedule</h2>
          <CalendarIcon className="w-5 h-5 text-text-secondary" />
        </div>

        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="animate-pulse flex items-center gap-4 p-4 rounded-lg bg-background"
            >
              <div className="flex-1">
                <div className="h-4 bg-text-muted rounded w-3/4 mb-2" />
                <div className="h-3 bg-text-muted rounded w-1/4" />
              </div>
              <div className="w-16 h-6 bg-text-muted rounded-full" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface-light rounded-xl shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-text-primary">Today's Schedule</h2>
        <CalendarIcon className="w-5 h-5 text-text-secondary" />
      </div>

      <div className="space-y-4">
        {events.map((event) => (
          <div
            key={event.id}
            className="flex items-center gap-4 p-4 rounded-lg bg-background hover:bg-primary-50 
                     transition-colors"
          >
            <div className="flex-1">
              <h3 className="text-sm font-medium text-text-primary">{event.title}</h3>
              <p className="text-sm text-text-secondary">
                {format(new Date(event.start), 'h:mm a')} - {format(new Date(event.end), 'h:mm a')}
              </p>
              {event.location && (
                <p className="text-xs text-text-muted mt-1">{event.location}</p>
              )}
            </div>

            <span
              className={`px-2 py-1 rounded-full text-xs font-medium ${getEventTypeStyle(event.type)}`}
            >
              {event.type}
            </span>
          </div>
        ))}

        {events.length === 0 && (
          <div className="text-center py-8">
            <p className="text-text-secondary">No events scheduled for today</p>
          </div>
        )}
      </div>
    </div>
  );
}